"use client";

import { cn } from "@jesus-central-church/shared";
import { Image } from "@jesus-central-church/ui";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { StoryblokServerComponent } from "@storyblok/react/rsc";
import type { SbBlokHero } from "@storyblok/types/287435740670216/storyblok-components";
import { motion } from "motion/react";

export function BlokHeroComponent({
  blok,
  pageData,
}: {
  blok: SbBlokHero;
  pageData: { locale: string };
}) {
  // Split title into words for staggered animation
  const titleWords = blok.title?.split(" ") || [];

  return (
    <div
      {...storyblokEditable(blok as SbBlokData)}
      className={cn(
        "relative h-[80vh] min-h-[550px] max-h-[900px] w-full overflow-hidden",
      )}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <Image
          src={blok.image.filename!}
          alt={blok.image.alt || "Afbeelding Jesus Central"}
          fill
          className="object-cover"
          sizes="100vw"
          priority
          fetchPriority="high"
          quality={90}
        />

        {/* Single overlay with combined gradients for better LCP */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to top, rgba(22,22,21,1) 0%, rgba(22,22,21,0.2) 50%, transparent 100%),
              linear-gradient(to bottom, rgba(22,22,21,0.5) 0%, transparent 30%),
              linear-gradient(to right, rgba(22,22,21,0.3) 0%, transparent 20%, transparent 80%, rgba(22,22,21,0.3) 100%),
              rgba(22,22,21,0.5)
            `.replaceAll(/\s+/g, " "),
          }}
        />
      </div>

      {/* Decorative accent line - left side */}
      <div className="pointer-events-none absolute bottom-0 left-6 top-1/2 w-px bg-gradient-to-b from-transparent via-strategy-gold/20 to-strategy-gold/40 md:left-12" />

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col">
        <div className="mt-auto grid gap-6 px-6 pb-10 md:grid-cols-2 md:gap-12 md:px-12 md:pb-12 lg:gap-16 lg:pb-16">
          {/* Left Column - Text Content */}
          <div className="flex flex-col justify-end">
            {/* Accent line above title */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              className="mb-6 h-1 w-16 origin-left bg-strategy-gold md:mb-8 md:w-20"
            />

            {/* Title - brutalist, word-by-word animation */}
            <h1 className="mb-4 md:mb-6">
              {titleWords.map((word, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, y: 60, rotateX: -45 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.1 + index * 0.08,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  className="mr-3 inline-block font-heading text-4xl font-black uppercase leading-[0.9] tracking-tight text-freedom sm:text-5xl md:mr-4 md:text-6xl lg:text-7xl"
                >
                  {word}
                </motion.span>
              ))}
            </h1>

            {/* Subtitle */}
            {blok.subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className="max-w-xl text-base leading-relaxed text-freedom/80 md:text-lg"
              >
                {blok.subtitle}
              </motion.p>
            )}

            {/* CTA buttons */}
            {blok.buttons && blok.buttons.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.55,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className="mt-8 flex flex-col gap-4 sm:flex-row md:mt-10"
              >
                {blok.buttons.map((button) => (
                  <StoryblokServerComponent
                    blok={button}
                    key={button._uid}
                    pageData={pageData}
                  />
                ))}
              </motion.div>
            )}
          </div>

          {/* Right Column - Custom Blok Content */}
          {blok.block && blok.block.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.3,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="flex items-end justify-center md:justify-end"
            >
              {blok.block.map((nestedBlok) => (
                <StoryblokServerComponent
                  blok={nestedBlok}
                  key={nestedBlok._uid}
                  pageData={pageData}
                />
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-freedom/10" />
    </div>
  );
}
